/// <amd-module name="DistributedTaskUI/ServiceEndpoints/Details/Stores/ServiceEndpointStore" />
import { IServiceEndpointDetails } from "DistributedTaskUI/ServiceEndpoints/Details/Source/ServiceEndpointsSource";
import { ServiceEndpoint, ServiceEndpointExecutionRecord, ServiceEndpointAuthenticationScheme, ServiceEndpointType } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";
import { ServiceEndpointActionsHub } from "DistributedTaskUI/ServiceEndpoints/Details/Actions/ServiceEndpointActionsHub";
import { ActionsHubManager } from "DistributedTask/Flux/ActionsHubManager";
import { StoreBase } from "DistributedTask/Flux/Store";
import { EndpointSchemeConversionSuccessful } from "DistributedTaskUI/ServiceEndpoints/Details/Constants";
import * as Utils_String from "VSS/Core/Util/String";
import * as EndpointConstants from "ServiceEndpoints/Client/Constants/ServiceEndpoint";

export interface IServiceEndpointComponentState {
    serviceEndpoint: ServiceEndpoint;
    eligibleSchemaUpgrade: ServiceEndpointAuthenticationScheme;
    eligibleSchemaDowngrade: ServiceEndpointAuthenticationScheme;
    serviceEndpointExecutionHistoryRecords: ServiceEndpointExecutionRecord[];
    serviceEndpointType: ServiceEndpointType;
    serviceEndpointOwnerEmail: string;
    serviceEndpointNotFound: boolean;
    dataLoaded: boolean;
    editingEnabled: boolean;
    convertingToSecretEnabled: boolean;
    errorMessage: string;
    conversionStatus: ConversionStatus;
    oidcFederationSubject: string;
    oidcFederationIssuer: string;
    itemCopiedToClipboard?: string;
    monitorUpgradeProcess: boolean;
}

export enum ConversionStatus {
    UNAVAILABLE,
    AVAILABLE,
    IN_PROGRESS,
    FAILED,
    SUCCESSFUL
}

export class ServiceEndpointStore extends StoreBase {
    constructor() {
        super("");
        this._state = ServiceEndpointStore.getDefaultState();
        this._actions = ActionsHubManager.GetActionsHub<ServiceEndpointActionsHub>(ServiceEndpointActionsHub);
        this._actions.initializeServiceEndpointHubData.addListener(this._initializeServiceEndpointHubData);
        this._actions.setServiceEndpointOwnerEmail.addListener(this._setServiceEndpointOwnerEmail);
        this._actions.setServiceEndpointNotFoundFlag.addListener(this._setServiceEndpointNotFoundFlag);
        this._actions.updateServiceEndpointSharedFlag.addListener(this._updateServiceEndpointSharedFlag);
        this._actions.updateServiceEndpointAuthenticationScheme.addListener(this._updateServiceEndpointAuthenticationScheme);
        this._actions.setMonitorSchemeConversionProgress.addListener(this._monitorUpgradeProcess);
        this._actions.setEditingEnabled.addListener(this._setEditingEnabled);
    }

    public static getKey(): string {
        return "service-endpoints-hub-store";
    }

    public disposeInternal(): void {
        this._actions.initializeServiceEndpointHubData.removeListener(this._initializeServiceEndpointHubData);
        this._actions.setServiceEndpointOwnerEmail.removeListener(this._setServiceEndpointOwnerEmail);
        this._actions.setServiceEndpointNotFoundFlag.removeListener(this._setServiceEndpointNotFoundFlag);
        this._actions.updateServiceEndpointSharedFlag.removeListener(this._updateServiceEndpointSharedFlag);
        this._actions.updateServiceEndpointAuthenticationScheme.removeListener(this._updateServiceEndpointAuthenticationScheme);
        this._actions.setMonitorSchemeConversionProgress.removeListener(this._monitorUpgradeProcess);
        this._actions.setEditingEnabled.removeListener(this._setEditingEnabled);
    }

    public getState(): IServiceEndpointComponentState {
        return this._state;
    }

    public static getDefaultState(): IServiceEndpointComponentState {
        return {
            serviceEndpoint: null,
            serviceEndpointExecutionHistoryRecords: [],
            serviceEndpointType: null,
            serviceEndpointOwnerEmail: "",
            serviceEndpointNotFound: true,
            dataLoaded: false,
            editingEnabled: true,
            convertingToSecretEnabled: false,
            eligibleSchemaUpgrade: null,
            eligibleSchemaDowngrade: null,
            conversionStatus: ConversionStatus.UNAVAILABLE,
            errorMessage: "",
            itemCopiedToClipboard: null,
            oidcFederationSubject: "",
            oidcFederationIssuer: "",
            monitorUpgradeProcess: false
        };
    }

    private _initializeServiceEndpointHubData = (data: IServiceEndpointDetails) => {
        if (data) {
            this._state.serviceEndpoint = data.serviceEndpoint;
            if (!this._state.serviceEndpoint.authorization.parameters) {
                this._state.serviceEndpoint.authorization.parameters = {};
            }
            this._state.serviceEndpointExecutionHistoryRecords = data.serviceEndpointExecutionHistory;
            this._state.serviceEndpointType = data.serviceEndpointType;
            this._state.serviceEndpointNotFound = false;
            this._state.dataLoaded = true;
            this._state.conversionStatus = data.eligibleSchemaUpgrade ? this._getConversionStatus(data.serviceEndpoint) : ConversionStatus.UNAVAILABLE;
            this._state.eligibleSchemaUpgrade = data.eligibleSchemaUpgrade;
            this._state.eligibleSchemaDowngrade = data.eligibleSchemaDowngrade;

            if (!!data.eligibleSchemaUpgrade) {
                this._state.oidcFederationSubject = data.eligibleSchemaUpgrade.properties[EndpointConstants.AzureEndpointConstants.WorkloadIdentityFederationSubject];
                this._state.oidcFederationIssuer = data.eligibleSchemaUpgrade.properties[EndpointConstants.AzureEndpointConstants.WorkloadIdentityFederationIssuer];
            } else {
                this._state.oidcFederationSubject = this._state.serviceEndpoint.authorization.parameters[EndpointConstants.AzureEndpointConstants.WorkloadIdentityFederationSubject];
                this._state.oidcFederationIssuer = this._state.serviceEndpoint.authorization.parameters[EndpointConstants.AzureEndpointConstants.WorkloadIdentityFederationIssuer];
            }

            this._state.editingEnabled = this._state.conversionStatus !== ConversionStatus.IN_PROGRESS &&
                !Utils_String.equals(this._state.serviceEndpoint.data[EndpointConstants.AzureEndpointConstants.IsDraft], "True", true);

            this._state.convertingToSecretEnabled =
                data.serviceEndpoint.type === EndpointConstants.AzureEndpointConstants.AzureRmEndpointType &&
                Utils_String.equals(
                    data.serviceEndpoint.authorization.scheme,
                    EndpointConstants.EndpointAuthorizationSchemes.WorkloadIdentityFederation,
                    true
                ) &&
                data.serviceEndpoint.data[EndpointConstants.AzureEndpointConstants.RevertSchemeDeadline] ===
                    EndpointConstants.AzureEndpointConstants.RevertSchemeDeadlineExpired;

            this.emitChanged();
        }
    }

    private _setEditingEnabled = (editingEnabled: boolean) => {
        this._state.editingEnabled = editingEnabled;
        this.emitChanged();
    }

    private _setServiceEndpointOwnerEmail = (ownerEmail: string) => {
        this._state.serviceEndpointOwnerEmail = ownerEmail;
        this.emitChanged();
    }

    private _setServiceEndpointNotFoundFlag = (serviceEndpointNotFound: boolean) => {
        this._state.serviceEndpointNotFound = serviceEndpointNotFound;
        this.emitChanged();
    }

    private _updateServiceEndpointSharedFlag = (serviceEndpoint: ServiceEndpoint) => {
        this._state.serviceEndpoint.isShared = serviceEndpoint.isShared;
        this.emitChanged();
    }

    private _updateServiceEndpointAuthenticationScheme = (conversionStatus: string) => {
        this._state.conversionStatus = this._parseConversionStatus(conversionStatus);
        this.emitChanged();
    }

    private _monitorUpgradeProcess = (monitor: boolean) => {
        this._state.monitorUpgradeProcess = monitor;
        this.emitChanged();
    }

    private _getConversionStatus = (serviceEndpoint: ServiceEndpoint) => {
        if (serviceEndpoint.operationStatus) {
            return this._parseConversionStatus(serviceEndpoint.operationStatus.statusMessage);
        }
        return ConversionStatus.AVAILABLE;
    }

    private _parseConversionStatus = (status: string) => {
        switch (status) {
            case EndpointConstants.AzureEndpointConstants.ConvertAuthenticationSchemeInProgressStatus:
                return ConversionStatus.IN_PROGRESS;
            case EndpointConstants.AzureEndpointConstants.ConvertAuthenticationSchemeFailedStatus:
                return ConversionStatus.FAILED;
            case EndpointSchemeConversionSuccessful:
                return ConversionStatus.SUCCESSFUL;
            default:
                return ConversionStatus.AVAILABLE;
        }
    }

    private _state: IServiceEndpointComponentState;
    private _actions: ServiceEndpointActionsHub;
}
